import React ,{ useState, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseIcon from '@mui/icons-material/Pause';
import { useTranslation } from 'react-i18next'; // استيراد الترجمة

const MediaGallery = ({ mediaItems }) => {
  const { t } = useTranslation(); // تفعيل الترجمة (للاستخدام المستقبلي أو الألتس)
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  
  const videoRefs = useRef([]);
  const controlsTimeoutRef = useRef(null);

  const startControlsTimer = () => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const activateMedia = (index) => {
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index) {
        video.pause();
        video.currentTime = 0;
      }
    });

    setSelectedIndex(index);
    const newItem = mediaItems[index];

    if (newItem.type === 'video') {
      setIsLoading(true);
      setTimeout(() => {
        const currentVideo = videoRefs.current[index];
        if (currentVideo) {
          currentVideo.play()
            .then(() => {
              setIsPlaying(true);
              setIsLoading(false);
              startControlsTimer();
            })
            .catch((err) => {
              console.warn("Autoplay prevented or failed:", err);
              setIsLoading(false);
              setIsPlaying(false);
            });
        }
      }, 100);
    } else {
      setIsPlaying(false);
      setIsLoading(false);
      setShowControls(true);
    }
  };

  const handleMainScreenClick = () => {
    const currentVideo = videoRefs.current[selectedIndex];
    if (mediaItems[selectedIndex].type === 'video' && currentVideo) {
      if (currentVideo.paused) {
        currentVideo.play();
        setIsPlaying(true);
        startControlsTimer();
      } else {
        currentVideo.pause();
        setIsPlaying(false);
        setShowControls(true);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      
      {/* الشاشة الرئيسية */}
      <Paper 
        elevation={6}
        onClick={handleMainScreenClick}
        onMouseMove={() => { setShowControls(true); if(isPlaying) startControlsTimer(); }}
        sx={{ 
          position: 'relative',
          width: '100%',
          height: { xs: 250, sm: 350, md: 450 },
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'custom.whitePure',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: mediaItems[selectedIndex].type === 'video' ? 'pointer' : 'default'
        }}
      >
        {mediaItems.map((item, index) => (
          <Box
            key={index}
            sx={{
              width: '100%',
              height: '100%',
              display: index === selectedIndex ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              
            }}
          >
            {item.type === 'image' ? (
              <Box
                component="img"
                src={item.image}
                alt={item.label} // هنا يتم تمرير النص المترجم من الأب
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                src={item.videoSrc}
                poster={item.image}
                playsInline
                webkit-playsinline="true"
                muted={false}
                preload="auto"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onWaiting={() => setIsLoading(true)}
                onPlaying={() => setIsLoading(false)}
                onEnded={() => { setIsPlaying(false); setShowControls(true); }}
              />
            )}
          </Box>
        ))}

        {isLoading && (
          <Box sx={{ position: 'absolute', zIndex: 3 }}>
            <CircularProgress color="secondary" size={60} thickness={4} />
          </Box>
        )}

        {!isLoading && mediaItems[selectedIndex].type === 'video' && (showControls || !isPlaying) && (
          <Box
            sx={{
              position: 'absolute',
              color: 'white',
              zIndex: 2,
              pointerEvents: 'none',
              transition: 'opacity 0.3s'
            }}
          >
            {isPlaying ? 
              <PauseIcon sx={{ fontSize: 90, opacity: 0.6 }} /> : 
              <PlayCircleOutlineIcon sx={{ fontSize: 90, opacity: 0.8 }} />
            }
          </Box>
        )}

        {/* الشريط السفلي للعنوان */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            background: `linear-gradient(transparent, ${alpha(theme.palette.custom.blackPure, 0.8)})`,
            p: 2,
            color: 'white',
            zIndex: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.5s'
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {mediaItems[selectedIndex].label} {/* النص المترجم القادم من Parent */}
          </Typography>
        </Box>
      </Paper>

      {/* شريط المصغرات */}
      <Box 
        sx={{ 
          display: 'flex', gap: 2, width: '100%', overflowX: 'auto', p: 0.5,
          justifyContent: 'center',
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'custom.mediaScrollbarThumb', borderRadius: '3px' }
        }}
      >
        {mediaItems.map((item, index) => (
          <Box
            key={index}
            onMouseEnter={() => activateMedia(index)}
            onClick={() => activateMedia(index)}
            sx={{
              width: 90, height: 65, borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
              border: index === selectedIndex ? `3px solid ${theme.palette.secondary.main}` : '2px solid transparent',
              opacity: index === selectedIndex ? 1 : 0.6,
              transform: index === selectedIndex ? 'scale(1.05)' : 'scale(1)',
               transition: 'all 0.3s ease-in-out', 

            filter: index === selectedIndex ? 'grayscale(0%)' : 'grayscale(100%)', flexShrink: 0, position: 'relative'
            }}
          >
            <Box component="img" src={item.image} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {item.type === 'video' && (
              <PlayCircleOutlineIcon sx={{
               
             position: 'absolute',
              top: '50%', 
              left: '50%',
              
               transform: 'translate(-50%, -50%)',
                color: 'white',
                 fontSize: 24 }} 
                 />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MediaGallery;